import { useEffect, useMemo, useState, type ReactNode } from "react";
import { matchColorCrosswalk, type ColorCrosswalkModifier } from "@/lib/manufacturing/colorCrosswalk";
import { selectedCrosswalkModifiers, selectedCrosswalkSource, type ColorCrosswalkDraft } from "@/lib/manufacturing/colorCrosswalkConclusion";
import { assignFinishAcrossFamily } from "@/lib/manufacturing/colorCrosswalkFamilySave";
import { partUseFamily, proposeColorCrosswalkCards, type ColorCrosswalkCard } from "@/lib/manufacturing/colorCrosswalkPropose";
import { updateManufacturingMaterialUseMember } from "@/lib/manufacturing/materialUsesClient";
import { normalizeFinishCode } from "@/lib/manufacturing/finishCode";
import {
  deleteColorCrosswalk,
  listColorCrosswalk,
  upsertInventoryMatchRule,
  type ColorCrosswalkRecord,
  type InventoryMatchRule,
  type InventoryOutlookReport,
} from "@/lib/manufacturing/inventoryMatchClient";
import { InventoryOutlookColorCardExport } from "./InventoryOutlookOrderMappingExport";
import { InventoryOutlookColorCrosswalkCard, type FinishPick } from "./InventoryOutlookColorCrosswalkCard";

const detailsClass = "rounded-xl border border-gray-200 dark:border-zinc-700";
const summaryClass = "cursor-pointer px-3 py-2 text-sm font-semibold";

const FIELD_LABEL: Record<string, string> = {
  series: "Series",
  roof_style: "Roof style",
  package: "Package",
  roof_type: "Roof type",
  roof_color: "Roof color",
  siding_type: "Siding type",
  siding_color: "Siding color",
  trim_color: "Trim color",
  addon_name: "Add-on",
};

function modifierLabel(modifier: ColorCrosswalkModifier): string {
  const name = FIELD_LABEL[modifier.field] ?? modifier.field;
  if (modifier.op === "contains") return `${name} contains ${modifier.value}`;
  return `${name} is ${modifier.value}`;
}

function cardBag(card: ColorCrosswalkCard, field: string, value: string) {
  const fields: Record<string, string> = { [field]: value };
  const addons: Array<{ name: string; quantity: number }> = [];
  for (const modifier of card.modifiers) {
    if (modifier.field === "addon_name") addons.push({ name: modifier.value, quantity: 1 });
    else fields[modifier.field] = modifier.value;
  }
  return { fields, addons };
}

function cardIsOpen(card: ColorCrosswalkCard, saved: readonly ColorCrosswalkRecord[]): boolean {
  const choices = card.color_choices.length > 0 ? card.color_choices : [{ field: card.source_field, value: card.source_value }];
  return choices.some(
    (choice) => matchColorCrosswalk(saved, cardBag(card, choice.field, choice.value), card.finish_channel).status !== "match",
  );
}

export function InventoryOutlookColorCrosswalk({
  report,
  toolbar,
  skuRules = [],
  onRemoveSkuRule,
  onSkuRulesSaved,
}: {
  report: InventoryOutlookReport;
  toolbar?: ReactNode;
  skuRules?: InventoryMatchRule[];
  onRemoveSkuRule?: (id: string) => void;
  onSkuRulesSaved?: (rules: InventoryMatchRule[]) => void;
}) {
  const [saved, setSaved] = useState<ColorCrosswalkRecord[]>([]);
  const [drafts, setDrafts] = useState<Record<string, ColorCrosswalkDraft>>({});
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void listColorCrosswalk().then((out) => {
      if (!out.ok) {
        setError(out.error);
        return;
      }
      setSaved(out.data.rows.filter((row) => row.is_active));
    });
  }, []);

  const cards = useMemo(() => {
    const proposed = proposeColorCrosswalkCards(
      (report.gaps ?? []).map((gap) => ({
        unit_id: gap.unit_id,
        material_use_id: gap.material_use_id,
        use_code: gap.use_code,
        finish_channel: gap.finish_channel ?? null,
        aspects: gap.aspects,
      })),
    );
    return proposed.filter((card) => cardIsOpen(card, saved));
  }, [report.gaps, saved]);

  function draftFor(card: ColorCrosswalkCard): ColorCrosswalkDraft {
    return (
      drafts[card.key] ?? {
        seriesOn: card.modifiers.length > 0,
        extraSeries: [],
        optional: [],
        finishCode: "",
        sourceField: card.source_field,
        sourceValue: card.source_value,
        pendingMemberId: "",
      }
    );
  }

  function patchDraft(card: ColorCrosswalkCard, patch: Partial<ColorCrosswalkDraft>) {
    setDrafts((prev) => ({ ...prev, [card.key]: { ...draftFor(card), ...prev[card.key], ...patch } }));
  }

  function siblingSeries(card: ColorCrosswalkCard, draft: ColorCrosswalkDraft): string[] {
    const source = selectedCrosswalkSource(card, draft);
    const color = normalizeFinishCode(source.value);
    const values = new Set<string>();
    for (const gap of report.gaps ?? []) {
      if (normalizeFinishCode(gap.finish_channel ?? "") !== card.finish_channel) continue;
      if (partUseFamily(gap.use_code) !== card.use_family) continue;
      const actual = gap.aspects.find((row) => row.field === source.field)?.value ?? "";
      if (normalizeFinishCode(actual) !== color) continue;
      const series = gap.aspects.find((row) => row.field === "series")?.value.trim() ?? "";
      if (series && normalizeFinishCode(series) !== normalizeFinishCode(card.modifiers[0]?.value ?? "")) values.add(series);
    }
    return [...values].sort();
  }

  function finishPicks(card: ColorCrosswalkCard): FinishPick[] {
    const byCode = new Map<string, { uses: Set<string>; sample: string }>();
    const uncoded: FinishPick[] = [];
    const seenUncoded = new Set<string>();
    for (const option of report.finish_options ?? []) {
      if (!card.material_use_ids.includes(option.material_use_id)) continue;
      for (const member of option.members ?? []) {
        if (!member.finish_code) {
          const value = `member:${member.member_id}`;
          if (seenUncoded.has(value)) continue;
          seenUncoded.add(value);
          const name = [member.material_code, member.material_name].filter(Boolean).join(" — ") || member.material_id;
          uncoded.push({ value, label: `${name} (no finish code)`, memberId: member.member_id });
          continue;
        }
        const bucket = byCode.get(member.finish_code) ?? { uses: new Set<string>(), sample: "" };
        bucket.uses.add(option.material_use_id);
        if (!bucket.sample) bucket.sample = [member.material_code, member.material_name].filter(Boolean).join(" — ");
        byCode.set(member.finish_code, bucket);
      }
      for (const code of option.finish_codes) {
        const bucket = byCode.get(code) ?? { uses: new Set<string>(), sample: "" };
        bucket.uses.add(option.material_use_id);
        byCode.set(code, bucket);
      }
    }
    const coded = [...byCode.entries()].map(([code, bucket]) => ({
      value: code,
      label: bucket.uses.size > 1 ? `${code} · ${bucket.uses.size} lengths` : bucket.sample ? `${code} — ${bucket.sample}` : code,
      memberId: "",
    }));
    return [...coded, ...uncoded].sort((a, b) => a.label.localeCompare(b.label));
  }

  async function save(card: ColorCrosswalkCard) {
    const draft = draftFor(card);
    const finishCode = draft.finishCode.trim();
    if (!finishCode || finishCode.startsWith("member:")) return;
    const source = selectedCrosswalkSource(card, draft);
    const modifiers = selectedCrosswalkModifiers(card, draft);
    if (modifiers.some((modifier) => modifier.op === "one_of")) {
      setError("Save one series at a time so each length can take its own color variant.");
      return;
    }
    const { assignments, missing } = assignFinishAcrossFamily({
      materialUseIds: card.material_use_ids,
      options: report.finish_options ?? [],
      finishCode,
      pendingMemberId: draft.pendingMemberId ?? "",
    });
    if (assignments.length === 0) {
      setError(`No ${finishCode} variant on ${missing.join(", ")}.`);
      return;
    }
    setBusy(true);
    setError(null);
    if (draft.pendingMemberId) {
      const stamped = await updateManufacturingMaterialUseMember({
        member_id: draft.pendingMemberId,
        finish_code: finishCode,
      });
      if (!stamped.ok) {
        setBusy(false);
        setError(stamped.error);
        return;
      }
    }
    const indicators = [
      { field: source.field, op: "equals" as const, value: source.value },
      ...modifiers.map((modifier) => ({
        field: modifier.field,
        op: modifier.op === "contains" ? ("contains" as const) : ("equals" as const),
        value: modifier.value,
      })),
    ];
    const savedRules: InventoryMatchRule[] = [];
    for (const assignment of assignments) {
      const out = await upsertInventoryMatchRule({
        material_use_id: assignment.material_use_id,
        material_id: assignment.material_id,
        priority: 100,
        indicators,
      });
      if (!out.ok) {
        setBusy(false);
        setError(out.error);
        if (savedRules.length > 0) onSkuRulesSaved?.(savedRules);
        return;
      }
      savedRules.push(out.data.rule);
    }
    setBusy(false);
    onSkuRulesSaved?.(savedRules);
    const family = card.use_family.replace(/_/g, " ");
    setNote(
      missing.length > 0
        ? `Saved ${family} where a ${finishCode} variant exists. Still missing ${missing.join(", ")}.`
        : `Saved ${family}. Each length uses its own ${finishCode} variant. Generate the outlook again to move demand.`,
    );
  }

  async function remove(row: ColorCrosswalkRecord) {
    setError(null);
    const out = await deleteColorCrosswalk(row.id);
    if (!out.ok) {
      setError(out.error);
      return;
    }
    setSaved((prev) => prev.filter((item) => item.id !== row.id));
  }

  return (
    <div className="space-y-3">
      <details className={detailsClass}>
        <summary className={summaryClass}>Mapping rules ({saved.length + skuRules.length})</summary>
        <div className="space-y-2 border-t border-gray-200 px-3 py-3 dark:border-zinc-700">
          {saved.length + skuRules.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-zinc-400">No mapping rules yet.</p>
          ) : (
            <ul className="space-y-1">
              {skuRules.map((rule) => (
                <li key={rule.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-200 px-2 py-1 text-sm dark:border-zinc-700">
                  <span>
                    {rule.use_code || "Part use"}
                    {rule.indicators.length > 0 ? ` · ${rule.indicators.map((indicator) => `${FIELD_LABEL[indicator.field] ?? indicator.field} ${indicator.value}`).join(" · ")}` : ""}
                    {` → ${rule.material_code || rule.material_name || rule.material_id}`}
                  </span>
                  {onRemoveSkuRule ? (
                    <button type="button" className="text-sm text-red-700 dark:text-red-300" onClick={() => onRemoveSkuRule(rule.id)}>
                      Remove
                    </button>
                  ) : null}
                </li>
              ))}
              {saved.map((row) => (
                <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-200 px-2 py-1 text-sm dark:border-zinc-700">
                  <span>
                    {FIELD_LABEL[row.source_field] ?? row.source_field} {row.source_value}
                    {row.modifiers.length > 0 ? ` · ${row.modifiers.map(modifierLabel).join(" · ")}` : " · any situation"}
                    {` → ${row.finish_channel} / ${row.finish_code}`}
                  </span>
                  <button type="button" className="text-sm text-red-700 dark:text-red-300" onClick={() => void remove(row)}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </details>
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {note ? <p className="text-sm text-gray-700 dark:text-zinc-300">{note}</p> : null}
      <div className="flex flex-wrap items-center gap-2">
        {toolbar}
        <InventoryOutlookColorCardExport cards={cards} />
      </div>
      <details className={detailsClass}>
        <summary className={summaryClass}>Color mappings ({cards.length})</summary>
        <div className="space-y-3 border-t border-gray-200 px-3 py-3 dark:border-zinc-700">
          <p className="text-sm text-gray-600 dark:text-zinc-400">
            Each card is one part family and one color, such as roof color Pewter Gray on metal ag panel. Lengths of that family stay together. Saving maps each length to its own variant of the finish you pick.
          </p>
          {cards.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-zinc-400">No open color decisions. Part uses without a finish channel stay in the serial list.</p>
          ) : (
            <ul className="max-h-[28rem] space-y-2 overflow-y-auto">
              {cards.map((card) => (
                <InventoryOutlookColorCrosswalkCard
                  key={card.key}
                  card={card}
                  draft={draftFor(card)}
                  picks={finishPicks(card)}
                  series={siblingSeries(card, draftFor(card))}
                  busy={busy}
                  onPatch={(patch) => patchDraft(card, patch)}
                  onSave={() => void save(card)}
                />
              ))}
            </ul>
          )}
        </div>
      </details>
    </div>
  );
}
